import type { Order, Position, StrategyDescriptionTag } from '../services/api';
import type { LiveQuote, QuoteConnectionStatus } from '../market/QuoteStore';
import { getPricePresentation, quoteSubscriptionIdentifier } from '../market/pricePresentation.ts';

export type SecurityTypeFilter = 'ALL' | 'STK' | 'CFD' | 'FUT' | 'OPT';
export type PositionWithQuoteStatus = Position & { quote_status?: string };

export const securityTypeOptions: Array<{ value: SecurityTypeFilter; label: string }> = [
  { value: 'ALL', label: 'All assets' },
  { value: 'STK', label: 'Stocks' },
  { value: 'CFD', label: 'CFDs' },
  { value: 'FUT', label: 'Futures' },
  { value: 'OPT', label: 'Options' },
];

export const getPositionDisplaySymbol = (position: Position): string => {
  if (position.display_symbol) return position.display_symbol;
  if (position.local_symbol) return position.local_symbol;
  if (position.secType === 'FUT' && position.last_trade_date_or_contract_month) {
    return `${position.symbol} ${position.last_trade_date_or_contract_month}`;
  }
  return position.symbol;
};

export const getPositionStrategies = (position: Position): string[] => {
  if (position.strategies?.length) return position.strategies;
  if (position.strategy) return position.strategy.split(' + ').filter(Boolean);
  return ['UNASSIGNED'];
};

export const getPositionDisplayStrategies = (position: Position): string[] => {
  const displayName = position.custom_strategy_name?.trim();
  return displayName ? [displayName] : getPositionStrategies(position);
};

export const getNQMMPortfolioPositions = (positions: readonly Position[]): Record<string, number> => {
  const result: Record<string, number> = {};
  for (const position of positions) {
    const symbol = position.symbol?.trim().toUpperCase();
    const quantity = Number(position.quantity);
    if (!symbol || !Number.isFinite(quantity) || quantity === 0
      || String(position.secType || 'STK').toUpperCase() !== 'STK'
      || !getPositionDisplayStrategies(position).some((name) => name.trim().toUpperCase() === 'NQMM')) continue;
    result[symbol] = (result[symbol] || 0) + quantity;
  }
  return result;
};

export const getPositionMetadataKey = (position: Position): string => {
  if (position.position_key) return position.position_key;
  if (position.contract_key) return position.contract_key;
  if (Number(position.con_id) > 0) return `conid:${Number(position.con_id)}`;

  return `contract:${[
    position.secType,
    position.symbol,
    position.local_symbol,
    position.last_trade_date_or_contract_month,
    position.exchange,
    position.currency,
    position.trading_class,
    position.multiplier,
  ].map((value) => String(value || '').toUpperCase()).join('|')}`;
};

export const getPositionRowKey = (position: Position): string => getPositionMetadataKey(position);

export const getSecurityType = (item: Position | Order): string =>
  String(item.secType || 'STK').toUpperCase();

export const formatPlainNumber = (value?: number | null): string => {
  if (value === undefined || value === null || !Number.isFinite(Number(value))) return '-';
  return Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 });
};

export const getStrategyTagColor = (tag: StrategyDescriptionTag): string =>
  tag.color && /^#[0-9A-F]{6}$/i.test(tag.color) ? tag.color : '#14B8A6';

export const getPositionQuoteIdentifiers = (positions: readonly Position[]): string[] =>
  positions.map((position) => quoteSubscriptionIdentifier(position.symbol, {
    sec_type: position.secType,
    local_symbol: position.local_symbol,
  }));

export const addLiveQuoteToPosition = (
  position: Position,
  quote: LiveQuote | null | undefined,
  connectionStatus: QuoteConnectionStatus,
  positionsStale: boolean,
): PositionWithQuoteStatus => {
  if (quote) {
    const presentation = getPricePresentation({
      quote,
      connectionStatus,
      hasPrice: Number.isFinite(Number(quote.price)),
      streamed: true,
    });
    return {
      ...position,
      current_price: quote.price,
      price_source: 'MARKET_DATA_HUB',
      quote_stale: presentation.stale,
      quote_status: presentation.status,
    };
  }
  return positionsStale ? { ...position, quote_stale: true } : position;
};

export const getPositionStrategyOptions = (
  positions: readonly Position[],
): string[] => Array.from(new Set(
  positions.flatMap((position) => getPositionDisplayStrategies(position)),
)).sort();

export const getPositionStrategyNameOptions = (
  positions: readonly Position[],
): string[] => Array.from(new Set(
  [
    // Keep core strategy labels available for manual correction even when the
    // current open positions have not yet been attributed to that strategy.
    'ON',
    'ORB',
    'DT',
    'NQMM',
    'MORB',
    ...positions.flatMap((position) => [
      ...getPositionStrategies(position),
      position.custom_strategy_name || '',
    ]),
  ],
))
  .map((name) => name.trim())
  .filter((name) => name && name !== 'UNASSIGNED')
  .sort((left, right) => left.localeCompare(right));

export const filterPositions = <T extends Position>(
  positions: readonly T[],
  strategyFilter: string,
  securityTypeFilter: SecurityTypeFilter,
): T[] => positions.filter((position) => {
  const matchesStrategy = strategyFilter === 'ALL'
    || getPositionDisplayStrategies(position).includes(strategyFilter);
  const matchesSecurityType = securityTypeFilter === 'ALL'
    || getSecurityType(position) === securityTypeFilter;
  return matchesStrategy && matchesSecurityType;
});
